import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircle2, XCircle, Info, Loader2 } from 'lucide-react';
import {
  postSeatInvitationResponseForPublicRequest,
  clearSeatInvitationResponse,
  selectSeatInvitationResponse,
} from '../model';
import { AuthContainer } from '../components/AuthComponents';
import { Button } from '../components/ui/button';
import { ContactSupportButton } from '@client/components/ContactSupportButton';

export function SeatInvitationResponse() {
  const { subscriptionSeatId, invitedEmail, action } = useParams<{
    subscriptionSeatId: string;
    invitedEmail: string;
    action: string;
  }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const invitationResponse = useSelector(selectSeatInvitationResponse);

  // Validate action parameter
  const isValidAction = action === 'accept' || action === 'decline';

  useEffect(() => {
    // Clear any previous state on mount
    dispatch(clearSeatInvitationResponse());

    // Only proceed if we have valid parameters
    if (subscriptionSeatId && isValidAction) {
      dispatch(
        postSeatInvitationResponseForPublicRequest({
          subscriptionSeatId,
          response: action as 'accept' | 'decline',
        }),
      );
    }
  }, [subscriptionSeatId, action, isValidAction, dispatch]);

  const isAccept = action === 'accept';
  const isLoading = invitationResponse.status === 'loading';
  const isSuccess = invitationResponse.status === 'success';
  const isError = invitationResponse.status === 'error';

  // Loading messages
  const loadingMessage = isAccept ? 'Accepting your invitation...' : 'Processing your response...';

  // Success messages
  const successTitle = isAccept ? 'Invitation Accepted!' : 'Invitation Declined';
  const successMessage = isAccept
    ? 'Your seat has been successfully activated. You can now log in to access your Waterlily Planning account.'
    : 'The invitation has been declined. The subscription owner has been notified.';

  // CTA button config
  const ctaText = isAccept ? 'Go to Login' : 'Go to Home';
  const ctaPath =
    isAccept && invitedEmail
      ? `/auth/login?email_hint=${encodeURIComponent(invitedEmail)}`
      : isAccept
        ? '/auth/login'
        : '/';

  // Handle invalid action
  if (!isValidAction) {
    return (
      <AuthContainer title="">
        <div className="flex flex-col items-center gap-6 px-4 py-8">
          <XCircle className="h-16 w-16 text-red-500" />
          <div className="text-center">
            <h3 className="mb-2 text-xl font-semibold text-gray-900">Invalid Invitation Link</h3>
            <p className="text-base text-gray-600">
              The link you followed is not valid. Please check your email and try again.
            </p>
          </div>
          <Button
            onClick={() =>
              navigate(invitedEmail ? `/?email_hint=${encodeURIComponent(invitedEmail)}` : '/')
            }
            className="w-full rounded-full py-6 text-base"
          >
            Go to Home
          </Button>
        </div>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer
      title=""
      successState={isSuccess && isAccept}
      warningState={isSuccess && !isAccept}
    >
      <div className="flex flex-col items-center gap-6 px-4 py-8">
        {isLoading && (
          <>
            <Loader2 className="text-primary h-16 w-16 animate-spin" />
            <p className="text-lg font-medium text-gray-700">{loadingMessage}</p>
          </>
        )}

        {isSuccess && (
          <>
            <div className="flex items-center gap-3">
              {isAccept ? (
                <CheckCircle2 className="h-12 w-12 text-green-500" />
              ) : (
                <Info className="h-12 w-12 text-blue-500" />
              )}
              <h3 className="text-xl font-semibold text-gray-900">{successTitle}</h3>
            </div>
            <p className="max-w-md text-center text-base text-gray-600">{successMessage}</p>
            {invitationResponse.message && (
              <p className="text-center text-sm text-gray-500">{invitationResponse.message}</p>
            )}
            <Button
              onClick={() => navigate(ctaPath)}
              className="w-full rounded-full py-6 text-base"
            >
              {ctaText}
            </Button>
          </>
        )}

        {isError && (
          <>
            <XCircle className="h-16 w-16 text-red-500" />
            <div className="text-center">
              <h3 className="mb-2 text-xl font-semibold text-gray-900">Something went wrong</h3>
              <p className="max-w-md text-base text-gray-600">
                This link may be invalid or expired. Please contact support for assistance.
              </p>
            </div>
            <ContactSupportButton
              subject="Invalid Seat Invitation Link"
              fallbackEmail="help@joinwaterlily.com"
              className="w-full rounded-full py-6 text-base"
            >
              Contact Support
            </ContactSupportButton>
          </>
        )}
      </div>
    </AuthContainer>
  );
}
